// Angular Core Imports
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router';

// RXJS Imports
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, first, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';

// Angular Material Imports
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatTabGroup, MatTabsModule} from '@angular/material/tabs';

// Firebase Imports
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

// Third-party Imports
import moment from 'moment-timezone';

// Application-specific Imports
import {AppStateService} from '../../services/app-state.service';
import {DataService} from '../../services/data.service';
import {BudgetListTableService} from '../../services/budget-list-table.service';
import {AuthService} from '../../services/auth.service';
import {ConfigService} from '../../services/config.service';
import {environment} from '../../../environments/environment';

// Component-specific models, interfaces and dialogs
import {
    Budget,
    ISubBudgetTotals,
    Offer,
    OfferTemplate,
    SplittedCostTableElementV1,
    SplittedCostTableElementV2,
    SubBudget
} from '../../interfaces';
import {SummaryBalanceWrapper} from '../../interfaces/summaryBalance';
import {SubBudgetModel} from '../models/sub-budget';
import {BudgetModel} from '../models/budget-model';
import {BUDGET_SCOPES} from '../models/budget-scopes';
import {Company} from '../../opportunities/interfaces/company.interface';

import {OpenProjectDialogComponent} from '../open-project-dialog/open-project-dialog.component';
import {ChangeFinalofferDialogComponent} from '../change-finaloffer-dialog/change-finaloffer-dialog.component';
import {ArchiveProjectDialogComponent} from '../archive-project-dialog/archive-project-dialog.component';
import {ContactPickerDialogComponent} from '../contact-picker-dialog/contact-picker-dialog.component';
import {BudgetScopeDialogComponent} from '../budget-scope-dialog/budget-scope-dialog.component';
import {CompanyChooseDialogComponent} from '../company-choose-dialog/company-choose-dialog.component';
import {OffersHistoryDialogComponent} from '../offers-history-dialog/offers-history-dialog.component';
import {MergeBudgetDialogComponent} from '../merge-budget-dialog/merge-budget-dialog.component';
import {OpportunityStatuses} from '../../opportunities/interfaces/opportunity-statuses.enum';
import {OpportunityStages} from '../../opportunities/interfaces/opportunity-stages.enum';
import {MatCardModule} from '@angular/material/card';
import {CommonModule} from '@angular/common';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatButtonModule} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatTableModule} from '@angular/material/table';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {SummaryChartComponent} from '../components/summary-chart/summary-chart.component';
import {MarginFormV1Component} from '../components/margin-form/v1/margin-form-component-v1.component';
import {MarginFormV2Component} from '../components/margin-form/v2/margin-form-component-v2.component';
import {AssemblyCostsFormComponent} from '../components/assembly-costs/assemblyCosts-form.component';
import {ProductionCostsFormV1Component} from '../components/production-costs-form/v1/production-costs-form-component-v1.component';
import {ProductionCostsFormV2Component} from '../components/production-costs-form/v2/production-costs-form-component-v2.component';
import {SplittedCostsTableV1Component} from '../components/splitted-costs-table/v1/splitted-costs-table-component-v1.component';
import {SplittedCostsTableV2Component} from '../components/splitted-costs-table/v2/splitted-costs-table-component-v2.component';
import {SubBudgetResumeComponent} from '../sub-budget-resume/sub-budget-resume.component';
import {SubBudgetComponent} from '../sub-budget/sub-budget.component';
import {SubBudgetFormComponent} from '../components/sub-budget-form.component';
import {TimestampPipe} from '../../shared/pipes/timestamp.pipe';
import Timestamp = firebase.firestore.Timestamp;
import FieldValue = firebase.firestore.FieldValue;



@Component({
    selector: 'app-budget-detail',
    styleUrls: ['./budget-detail.component.css'],
    templateUrl: './budget-detail.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatProgressBarModule,
        RouterLink,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        MatTooltipModule,
        MatTableModule,
        MatSlideToggleModule,
        MatProgressSpinnerModule,
        SummaryChartComponent,
        MatTabsModule,
        MarginFormV1Component,
        MarginFormV2Component,
        AssemblyCostsFormComponent,
        ProductionCostsFormV1Component,
        ProductionCostsFormV2Component,
        SplittedCostsTableV1Component,
        SplittedCostsTableV2Component,
        SubBudgetResumeComponent,
        SubBudgetComponent,
        SubBudgetFormComponent,
        TimestampPipe
    ]
})
export class BudgetDetailComponent implements OnInit, OnDestroy {

    // #region Component Properties
    public budget: Budget;
    public exportBudget: Budget;  // BUDGET DELLA COMMESSA USA COLLEGATA (SE ESISTE)
    public importBudget: Budget; // BUDGET DELLA COMMESSA ITA COLLEGATA (SE EXPORT)
    public bId: string;
    public budgetFolderLink: string;
    public offerUrl;
    public budgetScope: string[] = [];
    public hasPreviousBudget = false;
    public hasNextBudget = false;
    public budgetNavPositionText = '';
    // #endregion

    // #region UI State
    public loading = false;
    public refreshingCostSummary = false;
    public isBudgetInEditableStatus = true;
    public areScopesSet = false;
    public removeExportLinkBtn = false;
    public createExportBudgetBtn = false;
    public selectedTab = 0;
    public showExport = true;
    private merging = false;
    private alreadyOpenedDialog = false;
    // #endregion

    // #region Observables and Subscriptions
    public sbColl$: Observable<Partial<SubBudget>[]>;
    public sbCollExport$: Observable<Partial<SubBudget>[]>;
    public tags$;
    public graphObs$: Observable<Map<String, ISubBudgetTotals>>;
    public thereArePendingJobs$: Observable<boolean>;
    public thereArePendingJobsExport$: Observable<boolean>;
    private unsubscribe$ = new Subject();
    private subBudgetValues = new BehaviorSubject(new Map<String, ISubBudgetTotals>());
    private exportBudget$ = new BehaviorSubject<Budget>(null);
    // #endregion

    // #region Data collections and summaries
    public sbCollNameMap = new Map<String, String>();
    public subBudgetValuesForChart;
    public splittedCostsTableDataIta: SplittedCostTableElementV1[] | SplittedCostTableElementV2[] = [];
    public splittedCostsTableDataExport: SplittedCostTableElementV1[] | SplittedCostTableElementV2[] = [];
    public costsTablesColumns = ['label', 'value', 'description'];
    public costsTableData = [];
    public costsTableDataExport = [];
    private balanceSummary: SummaryBalanceWrapper;
    private balanceExportSummary: SummaryBalanceWrapper;
    private activeSubBudgets = [];
    // #endregion

    // #region Filters
    public tagFilter: string[] = [];
    // #endregion

    // #region Permissions
    public permissions = {
        canAddSubBudget: false,
        canAskProjectAuth: false,
        canGrantProjectAuth: false,
        modifyAuthorizationsGranted: false,
        canDeleteBudget: false,
        canPrepareOffer: false,
        canAssignBudget: false,
        canSeeFigures: false,
        canOpenProject: false,
        canCreateReport: false,
        canChangeScopes: false,
        canChangeFinalOffer: false,
        canArchiveBudget: false
    };
    public budgetIsEmpty = true;

    get currentUserEmail() {
        return this.auth.currentUser?.email;
    }

    // #endregion

    // #region Configuration
    public offerTemplates = [];
    public offerExportTemplates = [];
    public cosmoEnabled: boolean;
    private globalSettings;
    // #endregion

    // #region Constants
    readonly BUDGET_SCOPES = BUDGET_SCOPES;
    private readonly logPrefix = '[BudgetDetailComponent]';
    // #endregion

    // #region ViewChild
    @ViewChild('subBudgetsTabs', {static: false}) subBudgetsTabs: MatTabGroup;

    // #endregion

    constructor(
        private router: Router,
        private dataService: DataService,
        private budgetListTableService: BudgetListTableService,
        private configService: ConfigService,
        private auth: AuthService,
        public appState: AppStateService,
        private route: ActivatedRoute,
        public dialog: MatDialog,
        private _snackbar: MatSnackBar) {
    }

    // #region Lifecycle Hooks
    public ngOnInit() {
        this.initializeComponent();
    }

    public ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
        this.subBudgetValues.complete();
        this.exportBudget$.complete();
    }

    // #endregion

    // #region Public Methods (Template Event Handlers)
    public goToPreviousBudget() {
        if (!this.hasPreviousBudget) {
            return;
        }
        const ids = this.budgetListTableService.getQuickBudgetListIds();
        const currentIndex = ids.indexOf(this.bId);
        if (currentIndex > 0) {
            const prevId = ids[currentIndex - 1];
            this.router.navigate(['/budget', prevId]);
        }
    }

    public goToNextBudget() {
        if (!this.hasNextBudget) {
            return;
        }
        const ids = this.budgetListTableService.getQuickBudgetListIds();
        const currentIndex = ids.indexOf(this.bId);
        if (currentIndex !== -1 && currentIndex < ids.length - 1) {
            const nextId = ids[currentIndex + 1];
            this.router.navigate(['/budget', nextId]);
        }
    }

    public scopeChange() {
        if (this.budgetScope.length) {
            this.dataService.addScopesToBudget(this.budget, this.budgetScope)
                .then(() => this._snackbar.open('Scopes updated successfully.', 'Close', {duration: 3000}))
                .catch(error => {
                    console.error(`${this.logPrefix} Error adding scopes to budget ${this.budget.id}:`, error);
                    this._snackbar.open('Failed to update scopes. Please try again.', 'Close', {duration: 5000});
                });
        }
        if (this.exportBudget) {
            this.dataService.addScopesToBudget(this.exportBudget, this.budgetScope)
                .then(() => this._snackbar.open('Export budget scopes updated successfully.', 'Close', {duration: 3000}))
                .catch(error => {
                    console.error(`${this.logPrefix} Error adding scopes to export budget ${this.exportBudget.id}:`, error);
                    this._snackbar.open('Failed to update export budget scopes. Please try again.', 'Close', {duration: 5000});
                });
        }
        if (this.importBudget) {
            this.dataService.addScopesToBudget(this.importBudget, this.budgetScope)
                .then(() => this._snackbar.open('Import budget scopes updated successfully.', 'Close', {duration: 3000}))
                .catch(error => {
                    console.error(`${this.logPrefix} Error adding scopes to import budget ${this.importBudget.id}:`, error);
                    this._snackbar.open('Failed to update import budget scopes. Please try again.', 'Close', {duration: 5000});
                });
        }
    }

    public filterChange() {
        this.dataService.patchBudget(this.budget.id, {
            filter: this.tagFilter
        }).then(() => {
            console.debug(`${this.logPrefix} Budget filter updated. Recalculating sub-budgets.`);
            this.activeSubBudgets.forEach(sb => {
                const calculate = this.checkFilters(sb.tags);
                this.dataService.updateSubBudgetField(this.budget.id, sb.id, {calculate}).then(() => {
                    const sbValMap = this.subBudgetValues.value;
                    const sbVal = sbValMap.get(sb.id);
                    if (sbVal) {
                        sbVal.calculate = calculate;
                        sbValMap.set(sb.id, sbVal);
                        this.subBudgetValues.next(sbValMap);
                    }
                });
            });
        });
    }

    public toggleCalculate(event, sb) {
        this.dataService.updateSubBudgetField(this.budget.id, sb.id, {
            calculate: this.checkFilters(event)
        });
    }

    public removeExportLink() {
        console.info(`${this.logPrefix} Removing export link from budget ${this.bId}.`);
        this.dataService.patchBudget(this.bId, {
            exportBudget: null
        });
    }

    public openAssignDialog(): void {
        const dialogRef = this.dialog.open(ContactPickerDialogComponent, {
            width: '350px'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            result.deliveryThr = moment(result.deliveryThr).toDate();
            this.dataService.assignBudget(
                result.user,
                this.bId,
                result.deliveryThr,
                result.noteToPrjMgr
            ).subscribe(data => {
                console.info(`${this.logPrefix} Budget assigned successfully.`, data);
                this._snackbar.open('Budget assigned successfully.', 'Close', {duration: 3000});
            }, error => {
                console.error(`${this.logPrefix} Error assigning budget:`, error);
                this._snackbar.open('Failed to assign budget. Please try again.', 'Close', {duration: 5000});
            });
        });
    }

    public reopenAssignDialog(): void {
        const dialogRef = this.dialog.open(ContactPickerDialogComponent, {
            width: '350px',
            data: {showOnlyUser: true}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            this.dataService.reassignBudget(
                result.user,
                this.bId
            ).subscribe(data => {
                console.info(`${this.logPrefix} Budget reassigned successfully.`, data);
                this._snackbar.open('Budget reassigned successfully.', 'Close', {duration: 3000});
            }, error => {
                console.error(`${this.logPrefix} Error reassigning budget:`, error);
                this._snackbar.open('Failed to reassign budget. Please try again.', 'Close', {duration: 5000});
            });
        });
    }

    public async createExportBudget() {
        if (this.budget.exportBudget) {
            console.warn(`${this.logPrefix} This budget already has a linked export budget.`);
            this._snackbar.open('This budget already has a linked export budget.', 'Close', {duration: 5000});
            return;
        }
        console.info(`${this.logPrefix} Creating export budget for budget ${this.budget.id}.`);

        const opportunityId = this.budget.opportunityIds[0];
        this.loading = true;
        try {
            await this.dataService.createExportBudget(this.budget.id, this.budget.budgetNr, opportunityId, this.budget.company).toPromise();
        } catch (err) {
            console.error(err);
            this._snackbar.open('Failed to create export budget. Please try again.', 'Close');
        } finally {
            this.loading = false;
        }
    }

    public openOffersHistoryDialog(budget: Budget) {
        this.dialog.open(OffersHistoryDialogComponent, {data: budget, width: '50vw'});
    }

    public chooseCompanyToPrepareOffer(template: any, isExport: boolean) {
        this.dialog.open(CompanyChooseDialogComponent, {disableClose: true, data: this.budget}).afterClosed().pipe(
            take(1),
            filter(v => !!v)
        ).subscribe(async dialogResult => {
            const budgetToUse = isExport ? this.exportBudget : this.budget;
            if (typeof dialogResult === 'boolean') {
                console.debug(`${this.logPrefix} Using default company to prepare offer.`);
                await this.prepareOffer(template, budgetToUse);
            } else {
                console.debug(`${this.logPrefix} Using selected company to prepare offer:`, dialogResult);
                await this.prepareOffer(template, budgetToUse, dialogResult);
            }
        });
    }

    public async prepareOffer(offerTemplate: OfferTemplate, inputBudget: Budget, companyToUse?: Company) {
        this.loading = true;

        let locale = 'it-IT';
        if (inputBudget?.export) {
            inputBudget.offer = this.offerDataExport(this.splittedCostsTableDataExport);
            locale = 'en-US';
        } else {
            this.budget.offer = this.offerData(this.splittedCostsTableDataIta);
        }

        const relatedOppId = this.budget.opportunityIds[0];
        if (!relatedOppId) {
            this._snackbar.open('Please link at least one opportunity to this budget first.', 'Close', {duration: 5000});
            this.loading = false;
            return;
        }

        console.info(`${this.logPrefix} Preparing offer for budget ${inputBudget.budgetNr} and related opportunity ${relatedOppId}.`);
        try {
            const opportunityRelated = await this.dataService.getOpportunityByOpportunityIdSingle(relatedOppId).toPromise();

            let o1;
            console.debug(`${this.logPrefix} Getting company and primary contact...`);
            if (companyToUse) {
                o1 = forkJoin([
                    of(companyToUse),
                    this.dataService.getPersonById(parseInt(companyToUse.primary_contact_id, 0))
                ]);
            } else if (inputBudget.company?.companyId && opportunityRelated.contactId) {
                o1 = forkJoin([
                    this.dataService.getCompanyById(inputBudget.company.companyId),
                    this.dataService.getPersonById(opportunityRelated.contactId)
                ]);
            } else {
                o1 = of([null, null]);
            }

            o1.pipe(switchMap((companyData) => {
                const company = companyData[0];
                const primaryContact = companyData[1];
                console.debug(`${this.logPrefix} Got company and primary contact from Copper:`, companyData);

                const offerData: any = {
                    company: companyToUse ? companyToUse.name : inputBudget.company.companyName,
                    project: opportunityRelated.opportunityName,
                    subject: ''
                };

                if (company) {
                    offerData.address = company.address?.street || '';
                    offerData.zip_code = company.address?.postal_code || '';
                    offerData.city = company.address?.city || '';
                    offerData.country = company.address?.country || '';
                }

                if (primaryContact) {
                    offerData.primary_contact = primaryContact.name;
                }

                offerData.date = moment.tz(new Date(), 'Europe/Rome').format(inputBudget ? 'MM/DD/YYYY' : 'DD/MM/YYYY');
                offerData.BU_number = inputBudget.budgetNr;
                offerData.chapter_3 = 'Offerta Economica';
                offerData.chapter_3_item_1 = 'Produzione';
                offerData.section_1_title = 'Produzione';

                try {
                    console.debug(`${this.logPrefix} Using offer template:`, offerTemplate);
                    offerData.idModello = offerTemplate.templateId;
                    locale = offerTemplate.locale || locale;
                    if (!offerData.idModello) {
                        throw new Error('Template ID not found for ' + offerTemplate);
                    }
                } catch (err) {
                    console.warn(`${this.logPrefix} Using default template because of error:`, err);
                    offerData.idModello = '1H5Ip_j_egwbLoYwO-fGIUOOr_32eoLwApt40sLFBj5o';
                }

                const printNumber = (n: number) => {
                    try {
                        return n ? n.toLocaleString(locale, {maximumFractionDigits: 2}) : '';
                    } catch (err) {
                        console.warn(`${this.logPrefix} Cannot convert value to string:`, n);
                        return '';
                    }
                };

                offerData.section_1_amount = printNumber(inputBudget.offer.productionAmount);
                offerData.section_2_amount = printNumber(inputBudget.offer.designAmount);
                offerData.section_3_amount = printNumber(inputBudget.offer.shippingAmount);
                offerData.section_4_amount = printNumber(inputBudget.offer.assemblyAmount);

                offerData.total_amount = printNumber(inputBudget.offer.offerAmount);
                offerData.drawings_weeks = 3;
                offerData.approval_weeks = 4;
                offerData.production_weeks = 10;
                offerData.subFolderName = 'Documenti';

                let driveFolderId = this.globalSettings.googleDrive.teamDriveInternalId;
                if (!environment.stage) {
                    console.debug(`${this.logPrefix} Production environment. Using budget Drive folder.`);
                    driveFolderId = this.budget.driveFolderId;
                } else {
                    console.debug(`${this.logPrefix} Staging environment. Using TEST folder: ${driveFolderId}`);
                }

                console.info(`${this.logPrefix} Creating offer file in folder: ${driveFolderId}`);
                offerData.idCartellaDestinazione = driveFolderId;
                offerData.nomeDocumento = `${inputBudget.budgetNr}-${opportunityRelated.opportunityName}`;

                console.debug(`${this.logPrefix} Sending final offerData to Google Script:`, {
                    scriptId: environment.offerScriptId,
                    data: offerData
                });
                return this.dataService.prepareOffer(offerData, environment.offerScriptId);
            }))
                .subscribe((d) => {
                    console.info(`${this.logPrefix} Offer script returned:`, d);
                    this.loading = false;

                    if (!d.response?.result?.documentUrl) {
                        this._snackbar.open('The offer was not created. Please check the details and try again.',
                            'Close',
                            {duration: 5000});
                        return;
                    }

                    const allOffers = inputBudget.allOffers || [];
                    allOffers.push({
                        offerUrl: String(d.response.result.documentUrl),
                        companyName: companyToUse ? companyToUse.name : inputBudget.company.companyName,
                        amount: inputBudget.offer.offerAmount
                    });
                    this.dataService.patchBudget(inputBudget.id, {
                        offerUrl: String(d.response.result.documentUrl),
                        allOffers: allOffers
                    }).then(() => {
                        console.info(`${this.logPrefix} Budget updated with a new offer.`);
                        this._snackbar.open('Offer created and budget updated successfully.', 'Close', {duration: 3000});
                    });
                }, (err) => {
                    this._snackbar.open('An error occurred while preparing the offer. Please try again.', 'Close', {duration: 10000});
                    this.loading = false;
                    console.error(`${this.logPrefix} Error preparing offer:`, err);
                }, () => {
                    this.loading = false;
                });
        } catch (error) {
            console.error(`${this.logPrefix} Failed to prepare offer:`, error);
            this._snackbar.open('An unexpected error occurred. Please try again later.', 'Close', {duration: 5000});
            this.loading = false;
        }
    }


    public updateSubBudgetValues($event: ISubBudgetTotals, sbId) {
        const sbValMap = this.subBudgetValues.value;
        sbValMap.set(sbId, $event);
        this.subBudgetValues.next(sbValMap);
    }

    public deleteSubBudget(subBudgetId: string) {
        const sbValMap = this.subBudgetValues.value;
        if (sbValMap.delete(subBudgetId)) {
            console.info(`${this.logPrefix} Sub-budget ${subBudgetId} deleted from local values.`);
        }
        this.subBudgetValues.next(sbValMap);
    }

    public deleteBudget() {
        this.appState.confirmDialog({confirmMessage: 'The current budget will be deleted.'})
            .then((result) => {
                if (result?.confirm) {
                    console.info(`${this.logPrefix} Deleting budget ${this.bId}.`);
                    const waitActions: Promise<any>[] = [];
                    waitActions.push(this.dataService.deleteBudgetApi(this.auth.currentUser.email, this.bId).toPromise());
                    if (this.importBudget) {
                        waitActions.push(
                            this.dataService.patchBudget(this.importBudget.id, {exportBudget: FieldValue.delete()})
                        );
                    }
                    Promise.all(waitActions).then(() => {
                        this.appState.infoDialog({infoMessage: 'The budget has been successfully deleted'});
                        this.router.navigate(['/budget']);
                    }, err => {
                        console.error(`${this.logPrefix} Error deleting budget:`, err);
                        this._snackbar.open('Failed to delete budget. Please try again.', 'Close', {duration: 5000});
                    });
                }
            });
    }

    public refreshCostSummary(budgetId: string) {
        this.refreshingCostSummary = true;
        console.info(`${this.logPrefix} Refreshing cost summary for budget: ${budgetId}`);
        this.dataService.runSummaryRecalculateJob(budgetId, this.exportBudget?.id)
            .subscribe(() => {
                console.info(`${this.logPrefix} Cost summary refresh completed for budget: ${budgetId}`);
                this._snackbar.open('Cost summary refreshed successfully.', 'Close', {duration: 3000});
                this.refreshingCostSummary = false;
            }, err => {
                console.error(`${this.logPrefix} Error refreshing cost summary for budget ${budgetId}:`, err);
                this._snackbar.open('Failed to refresh cost summary. Please try again.', 'Close', {duration: 5000});
                this.refreshingCostSummary = false;
            });
    }

    public openProject() {
        const isExport = !!this.budget.export;
        let projectNrITA = '';
        if (isExport && this.importBudget?.project) {
            projectNrITA = this.importBudget.project.projectNr;
        }
        const dialogRef = this.dialog.open(OpenProjectDialogComponent, {
            width: '600px',
            data: {
                budget: this.budget,
                user: this.budget.assignee,
                oData: this.offerData(this.splittedCostsTableDataIta),
                projectNrITA: projectNrITA
            }
        });

        dialogRef.afterClosed().pipe(
            filter(result => result))
            .subscribe(result => {
                console.info(`${this.logPrefix} Opening project with data:`, result);
                this.dataService.openProject(
                    this.bId,
                    result.projectNr,
                    result.finalOffer,
                    result.projectManager,
                    result.paymentMethod,
                    result.paymentMethodCode,
                    result.deliveryDate)
                    .subscribe(async data => {
                        console.info(`${this.logPrefix} Project opened successfully.`, data);
                        this._snackbar.open('Project opened successfully.', 'Close', {duration: 3000});
                    }, err => {
                        console.error(`${this.logPrefix} Error opening project:`, err);
                        this._snackbar.open('Failed to open project. Please try again.', 'Close', {duration: 5000});
                    });
            });
    }

    public async reOpenProject() {
        this.appState.confirmDialog({confirmMessage: `Budget ${this.budget?.budgetNr} will be re opened`})
            .then(async (result) => {
                if (result?.confirm) {
                    const newStatus = this.budget.project ? 'closed' : 'assigned';
                    console.info(`${this.logPrefix} Re-opening project for budget ${this.budget.id} with new status: ${newStatus}`);
                    try {
                        const data = await this.dataService.reopenProject(this.budget.id, newStatus).toPromise();
                        console.info(`${this.logPrefix} Project re-opened successfully.`, data);
                        this._snackbar.open('Project reopened', 'Close', {duration: 5000});
                    } catch (err) {
                        console.error(`${this.logPrefix} Error re-opening project:`, err);
                        this._snackbar.open('Failed to reopen project. Please try again.', 'Close', {duration: 5000});
                    }
                }
            });
    }

    public async getReportFile() {
        this.loading = true;
        try {
            const summarySnap = await this.dataService.getSimpleBudgetSummaryBalance(this.budget.id);
            if (!summarySnap.exists) {
                console.info(`${this.logPrefix} Summary balance does not exist. Creating it before generating PDF for budget ${this.budget.id}.`);
                await this.dataService.createSummaryBalance(this.budget.id).toPromise();
            }

            console.info(`${this.logPrefix} Creating PDF for budget ${this.budget.id}.`);
            await this.dataService.createSummaryPdf(this.budget.id).pipe(first()).toPromise();

            console.info(`${this.logPrefix} PDF creation process started.`);
            this._snackbar.open('Report generation successfully started.', 'Close', {duration: 3000});
        } catch (err) {
            console.error(`${this.logPrefix} Error getting report file:`, err);
            this._snackbar.open('An error occurred during report generation. Please check logs for details.', 'Close', {duration: 5000});
        } finally {
            this.loading = false;
        }
    }


    public archive() {
        if (this.budget.project) {
            this.archiveProject();
        } else {
            this.archiveBudget();
        }
    }

    public addSubBudget(sb: SubBudgetModel) {
        console.info(`${this.logPrefix} Adding sub-budget:`, sb);
        this.dataService.addSubBudget(this.bId, sb.description, sb.generatedBy)
            .subscribe((r) => {
                console.info(`${this.logPrefix} Sub-budget created successfully:`, r);
                this._snackbar.open('Sub-budget added successfully.', 'Close', {duration: 3000});
            }, err => {
                console.error(`${this.logPrefix} Error adding sub-budget:`, err);
                this._snackbar.open('Failed to add sub-budget. Please try again.', 'Close', {duration: 5000});
            });
    }

    public askBudgetModifyAuth() {
        console.info(`${this.logPrefix} Asking for modify permission for budget:`, this.budget.id);
        this.loading = true;
        const budget = {
            id: this.budget.id,
            budgetNr: this.budget.budgetNr,
            project: this.budget.project,
            company: this.budget.company
        };
        this.dataService.askBudgetModifyAuth(budget).pipe(first())
            .subscribe((res) => {
                    this.loading = false;
                    console.info(`${this.logPrefix} Authorization asked successfully.`, res);
                    this._snackbar.open('Authorization request sent.', 'Close', {duration: 3000});
                },
                (err) => {
                    this.loading = false;
                    console.warn(`${this.logPrefix} Error asking for authorization:`, err);
                    this._snackbar.open('Failed to ask for authorization. Please try again.', 'Close', {duration: 5000});
                });
    }

    public doneModifications() {
        console.info(`${this.logPrefix} Done modifications for budget:`, this.budget.id);
        const newPerms = {...this.budget.authGrants};
        newPerms[this.auth.currentUser.email] = null;
        this.dataService.patchBudget(this.budget.id, {authGrants: newPerms});
    }

    public ackBudgetModifyAuth(userEmail: string, isAuth: boolean) {
        this.loading = true;
        console.info(`${this.logPrefix} Acknowledging modify permission for budget ${this.budget.id} with auth=${isAuth} and user ${userEmail}.`);
        const budget = {
            id: this.budget.id,
            budgetNr: this.budget.budgetNr,
            project: this.budget.project,
            company: this.budget.company
        };
        this.dataService.ackBudgetModifyAuth(budget, userEmail, isAuth).pipe(first())
            .subscribe((res) => {
                    this.loading = false;
                    console.info(`${this.logPrefix} Authorization confirmed.`, res);
                    this._snackbar.open('Authorization granted for 48 hours', 'Close', {duration: 5000});
                },
                (err) => {
                    this.loading = false;
                    console.error(`${this.logPrefix} Error while authorizing order:`, err);
                    this._snackbar.open('Failed to grant authorization. Please try again.', 'Close', {duration: 5000});
                });
    }

    public updateCostsTables() {
        if (!this.budget || !this.budget.costSummary || !this.balanceSummary) {
            return;
        }
        this.costsTableData = [
            {
                label: 'Total Cost ITA',
                value: this.budget.costSummary.total,
                description: 'Internal cost (activities + materials)',
                currency: 'EUR'
            },
            {
                label: 'Total Offer ITA',
                value: this.balanceSummary.costSummary.budget.totalOffer ? this.balanceSummary.costSummary.budget.totalOffer : '',
                description: 'Total cost ITA + profit, gen.expenses, commissions, custom duties',
                currency: 'EUR'
            },
            {
                label: 'Final Offer ITA',
                value: this.budget.project ? this.budget.project.finalOfferOrder : '',
                description: 'Final contract value, manually specified',
                currency: 'EUR'
            }
        ];

        if (this.exportBudget && this.balanceExportSummary) {
            const totalOffer = this.balanceExportSummary.costSummary?.budget?.totalOffer;
            const totalCost = this.balanceExportSummary.costSummary?.budget?.totalCost;
            this.costsTableDataExport = [
                {
                    label: 'Total Cost USA',
                    valueCurrency: totalCost / this.exportBudget.export.exchangeRate,
                    value: totalCost,
                    description: 'Materials USA (includes Total Offer ITA) + Activities USA',
                    currency: 'USD'
                },
                {
                    label: 'Total Offer USA',
                    value: totalOffer ? totalOffer : '',
                    description: 'Total cost USA + margins USA',
                    currency: 'USD'
                },
                {
                    label: 'Final Offer USA',
                    value: this.exportBudget.project ? this.exportBudget.project.finalOfferOrder : '',
                    description: 'Final contract value, manually specified',
                    currency: 'USD'
                }
            ];
        }
    }

    public applyShowExport(checked: boolean) {
        this.showExport = checked;
    }

    public changeFinalOffer(isExport: boolean) {
        const selectedB = isExport ? this.exportBudget : this.budget;

        const finalOfferOrder = selectedB.project.finalOfferOrder;
        const finalOfferOrderChanges = selectedB.project.finalOfferOrderChanges;
        const dialogRef = this.dialog.open(ChangeFinalofferDialogComponent, {
            width: '450px',
            data: {finalOfferOrder, finalOfferOrderChanges, isExport}
        });

        dialogRef.afterClosed().pipe(
            filter(result => result))
            .subscribe(result => {
                console.info(`${this.logPrefix} Changing final offer with data:`, result);

                const updateObj = selectedB.project;
                const previousValue = updateObj.finalOfferOrder !== undefined ? updateObj.finalOfferOrder : 0;
                const audit = {
                    user: this.auth.currentUser.displayName,
                    date: new Date(),
                    prevValue: previousValue,
                    newValue: result.finalOfferOrder,
                    difference: Math.round((result.finalOfferOrder - previousValue + Number.EPSILON) * 100) / 100
                };
                updateObj.finalOfferOrder = result.finalOfferOrder;
                if (updateObj.finalOfferOrderChanges) {
                    updateObj.finalOfferOrderChanges.push(audit);
                } else {
                    updateObj.finalOfferOrderChanges = [audit];
                }
                const budgetInfo = {
                    export: selectedB.export,
                    projectNr: selectedB.project.projectNr,
                    id: selectedB.id
                };

                this.dataService.changeFinalOffer(budgetInfo, updateObj, audit, this.auth.currentUser).pipe(take(1)).subscribe(() => {
                    this.refreshCostSummary(selectedB.id);
                }, err => {
                    console.error(`${this.logPrefix} Failed to update final offer or send email:`, err);
                    this._snackbar.open('Failed to update final offer. Please try again.', 'Close', {duration: 5000});
                });
            });
    }

    public changeTab(label: string) {
        if (this.subBudgetsTabs) {
            const tabs = (this.subBudgetsTabs?._tabs as any)._results;
            const match = tabs?.find(x => x.textLabel?.toUpperCase() === label.toUpperCase());

            if (match) {
                this.subBudgetsTabs.selectedIndex = match.position + 1;
            }
        }
    }

    public mergeBudget() {
        const dialogRef = this.dialog.open(MergeBudgetDialogComponent, {
            width: '35vw',
            data: {
                budget: this.budget
            }
        });

        dialogRef.afterClosed().pipe(
            filter(result => result))
            .subscribe((budgetSelected: Budget) => {
                if (budgetSelected?.id) {
                    console.info(`${this.logPrefix} Merging budget ${this.bId} into ${budgetSelected.id}.`);
                    this.loading = true;
                    this.merging = true;
                    this.dataService.mergeBudgets(
                        this.bId,
                        budgetSelected.id,
                        this.budget.budgetNr)
                        .subscribe(() => {
                            this.loading = false;
                            this.merging = false;
                            this._snackbar.open('Budget merged successfully', 'Close', {duration: 5000});
                            this.router.navigate(['/budget', budgetSelected.id]);
                        }, err => {
                            this.loading = false;
                            this.merging = false;
                            console.error(`${this.logPrefix} Error merging budgets:`, err);
                            this._snackbar.open('Failed to merge budgets. Please try again.', 'Close', {duration: 5000});
                        });
                }
            });
    }

    // #endregion

    // #region Private Methods
    private initializeComponent() {
        this.budgetListTableService.quickBudgetListIds$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                if (this.bId) {
                    this.updateBudgetNavigationState(this.bId);
                }
            });
        this.setupBudgetStreams();
        this.setupTagStream();
        this.setupSubBudgetStream();
        this.setupConfigStream();
        this.setupGraphStream();
        this.sbCollExport$ = this.exportBudget$.pipe(
            switchMap(exportBudget => {
                if (exportBudget) {
                    return this.dataService.getSubBudgetCollection(exportBudget.id);
                }
                return of([]);
            }),
            takeUntil(this.unsubscribe$)
        );
    }

    private updateBudgetNavigationState(budgetId: string) {
        const ids = this.budgetListTableService.getQuickBudgetListIds();
        if (!ids || ids.length === 0 || !budgetId) {
            this.hasPreviousBudget = false;
            this.hasNextBudget = false;
            this.budgetNavPositionText = '';
            return;
        }
        const index = ids.indexOf(budgetId);
        if (index === -1) {
            this.hasPreviousBudget = false;
            this.hasNextBudget = false;
            this.budgetNavPositionText = '';
            return;
        }
        this.hasPreviousBudget = index > 0;
        this.hasNextBudget = index < ids.length - 1;
        this.budgetNavPositionText = `${index + 1} / ${ids.length}`;
    }

    private setupBudgetStreams() {
        const budget$ = this.route.paramMap.pipe(
            switchMap((params: ParamMap) => {
                this.bId = params.get('id');
                console.info(`${this.logPrefix} Getting budget with id: ${this.bId}`);
                this.resetVariables();
                this.updateBudgetNavigationState(this.bId);
                return this.dataService.getBudget(this.bId);
            }));

        this.thereArePendingJobs$ = this.route.paramMap.pipe(
            switchMap((params: ParamMap) =>
                this.dataService.getPendingJobs((params.get('id'))
                ).pipe(takeUntil(this.unsubscribe$),
                    map(j => j && j.length > 0))));


        const balanceSummary$ = this.route.paramMap.pipe(
            switchMap((params: ParamMap) =>
                this.dataService.getBudgetSummaryBalance(params.get('id'))
            ));

        const permissions$ = this.auth.userHasPermissions([
            'app.budget.assign',
            'app.budget.project.open',
            'app.budget.figures.read',
            'app.budget.write',
            'app.iproject.write',
            'app.budget.auth.ask',
            'app.budget.auth.grant',
            'app.budget.delete',
            'app.prepare.offer',
            'app.create.report',
            'app.budget.scope',
            'app.budget.changefinaloffer',
            'app.budget.edit.others',
            'app.budget.archive']);

        combineLatest([budget$, balanceSummary$, permissions$]).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(([budget, summary, permissions]) => {
            this.handleBudgetLoaded(budget, summary, permissions);
        }, (err) => {
            console.error(`${this.logPrefix} Error getting budget:`, err.message);
            this._snackbar.open('Failed to load budget data. Please refresh the page.', 'Close', {duration: 5000});
        });
    }

    private handleBudgetLoaded(b: Budget, summary: SummaryBalanceWrapper, permissions: boolean[]) {
        if (!b) {
            if (!this.merging) {
                this._snackbar.open('Unable to find this budget.', 'Close', {duration: 5000});
                this.router.navigate(['/budget']);
            }
            return;
        }
        console.info(`${this.logPrefix} Loaded budget:`, b);

        this.balanceSummary = summary;
        console.info(`${this.logPrefix} Loaded summary:`, summary);

        if (this.balanceSummary?.materialSummary.length > 0 ||
            this.balanceSummary?.activitySummary.length > 0) this.budgetIsEmpty = false;
        else this.budgetIsEmpty = true;

        this.budget = b;
        this.budgetScope = this.budget.scope || [];

        if (!this.budget.exportBudget) {
            this.exportBudget = null;
            this.exportBudget$.next(null);
        }

        if (BudgetModel.isAssignedUser(this.auth.currentUser, this.budget)) {
            if (!BudgetModel.areScopesSet(b) && !this.alreadyOpenedDialog) {
                this.openScopeDialog();
            }
        }

        this.updateFiltersAndPermissions(this.budget);
        this.updatePaymentStatus(this.budget);
        if (!b.export) {
            this.splittedCostsTableDataIta = b.splittedCostSummary;
        }

        if (b.export) {
            this.splittedCostsTableDataExport = b.splittedCostSummary;
            this.retrieveRelativeImport(b);
        } else if (b.exportBudget) {
            this.createExportBudgetBtn = false;
            this.retrieveRelativeExport(this.budget);
        } else if (b.project?.internal) {
            this.createExportBudgetBtn = false;
            this.removeExportLinkBtn = false;
        } else if (b.status === 'archived') {
            this.createExportBudgetBtn = false;
            this.removeExportLinkBtn = false;
        } else {
            this.createExportBudgetBtn = true;
            this.removeExportLinkBtn = false;
        }

        this.updateCostsTables();
        this.setPermissions(permissions);
    }

    private setPermissions(permissions: boolean[]) {
        const currentUser = this.auth.currentUser;
        const canAddSubBudget = BudgetModel.genericEditBudgetPermission(
            this.budget,
            currentUser,
            (permissions[3] || permissions[4]),
            permissions[12]);
        this.permissions = {
            canAssignBudget: permissions[0],
            canOpenProject: permissions[1],
            canSeeFigures: permissions[2],
            canAddSubBudget,
            canAskProjectAuth: permissions[5],
            canGrantProjectAuth: permissions[6],
            canDeleteBudget: permissions[7],
            canPrepareOffer: permissions[8],
            canCreateReport: permissions[9],
            canChangeScopes: permissions[10] || BudgetModel.isAssignedUser(currentUser, this.budget),
            modifyAuthorizationsGranted: this.hasModifyAuth(),
            canChangeFinalOffer: permissions[11],
            canArchiveBudget: permissions[13]
        };
        console.debug(`${this.logPrefix} Updated page permissions:`, this.permissions);
    }

    public hasModifyAuth(): boolean {
        if (!this.budget?.authGrants) return false;
        if (!this.budget.authGrants[this.auth.currentUser.email]) return false; // case null
        if (this.budget.authGrants[this.auth.currentUser.email] === 'no') return false;
        return this.budget.authGrants[this.auth.currentUser.email] !== 'waiting';
    }

    public getModifyAuth() {
        if (!this.budget?.authGrants) return false;
        if (!this.auth.currentUser?.email) return false;
        if (!this.budget.authGrants[this.auth.currentUser.email]) return false; // case null
        return this.budget.authGrants[this.auth.currentUser.email];
    }

    private openScopeDialog() {
        this.alreadyOpenedDialog = true;
        this.dialog.open(BudgetScopeDialogComponent, {
            disableClose: true,
            hasBackdrop: true
        }).afterClosed().pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(scopes => {
            this.alreadyOpenedDialog = false;
            this.dataService.addScopesToBudget(this.budget, scopes)
                .then(() => this._snackbar.open('Scopes added successfully.', 'Close', {duration: 3000}));
        });
    }

    private setupTagStream() {
        this.tags$ = this.route.paramMap.pipe(
            switchMap((params: ParamMap) =>
                this.dataService.getSubBudgetCollection(params.get('id'))))
            .pipe(
                takeUntil(this.unsubscribe$),
                map((subs: SubBudget[]) => {
                    const tags = subs.flatMap(sb => sb.tags || []).filter((value, index, self) => self.indexOf(value) === index);
                    this.tagFilter = this.tagFilter.filter(t => tags.includes(t));
                    return tags;
                })
            );
    }

    private setupSubBudgetStream() {
        this.sbColl$ = this.route.paramMap.pipe(
            switchMap((params: ParamMap) => this.dataService.getSubBudgetCollection(this.bId)),
            tap((sbc: SubBudget[]) => {
                sbc.forEach(sb => {
                    this.sbCollNameMap.set(sb.id, sb.description);
                });
            }),
            map(sbc => {
                const sbcF = sbc.filter(sb => !sb.deleted);
                this.activeSubBudgets = sbcF;
                return sbcF.map((sb) => ({
                    id: sb.id,
                    report: sb.report,
                    overrideEdit: sb.overrideEdit,
                    description: sb.description,
                    tags: sb.tags,
                    calculate: sb.calculate
                }));
            }),
            distinctUntilChanged((sbcEasy1, sbcEasy2) => JSON.stringify(sbcEasy1) === JSON.stringify(sbcEasy2))
        );
    }

    private setupConfigStream() {
        this.configService.settings.subscribe((sett) => {
            if (sett) {
                this.globalSettings = sett.value;
                try {
                    this.offerTemplates = sett.value.googleDrive.offerTemplates;
                    this.offerExportTemplates = sett.value.googleDrive.offerExportTemplates;
                    this.cosmoEnabled = sett.value.cosmo.enabled;
                } catch (err) {
                    console.warn(`${this.logPrefix} Could not set config values from settings:`, err);
                }
            }
        });
    }

    private setupGraphStream() {
        this.graphObs$ = this.updateGraph().pipe(takeUntil(this.unsubscribe$));
        this.graphObs$
            .pipe(debounceTime(2000))
            .subscribe(() => {
                console.debug(`${this.logPrefix} Fired graphic update.`);
            });
    }

    private retrieveRelativeImport(b: Budget) {
        this.dataService.getBudget(b.export.from.id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((bITA) => {
                this.importBudget = null;
                if (bITA && this.budget.id !== bITA.id) {
                    this.importBudget = bITA;
                    console.info(`${this.logPrefix} Retrieved Budget ITA:`, this.importBudget);
                    this.splittedCostsTableDataIta = this.importBudget.splittedCostSummary;
                }
            });
    }

    private retrieveRelativeExport(b: Budget) {
        this.dataService.getBudgetByRef(b.exportBudget)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((bUSARef) => {
                this.exportBudget = null;
                if (bUSARef) {
                    if (this.budget?.id !== bUSARef.id) {
                        this.exportBudget = bUSARef;
                        this.exportBudget$.next(this.exportBudget);
                        console.info(`${this.logPrefix} Retrieved relative Budget USA:`, this.exportBudget);
                        this.splittedCostsTableDataExport = this.exportBudget.splittedCostSummary;

                        this.sbCollExport$ = this.dataService
                            .getSubBudgetCollection(this.exportBudget.id)
                            .pipe(takeUntil(this.unsubscribe$));

                        this.thereArePendingJobsExport$ = this.dataService.getPendingJobs(this.exportBudget.id)
                            .pipe(
                                takeUntil(this.unsubscribe$),
                                map(j => j && j.length > 0)
                            );

                        this.dataService.getBudgetSummaryBalance(this.exportBudget.id)
                            .pipe(takeUntil(this.unsubscribe$))
                            .subscribe(result => {
                                this.balanceExportSummary = result;
                                this.updateCostsTables();
                            });

                        this.updateCostsTables();
                    }
                } else {
                    this.exportBudget = null;
                    this.exportBudget$.next(null);
                    this._snackbar.open('The linked export budget does not exist anymore.', 'Close', {duration: 5000});
                    this.removeExportLinkBtn = true;
                }
            });
    }

    private updateFiltersAndPermissions(b: Budget) {
        this.tagFilter = b.filter || [];
        this.offerUrl = b.offerUrl;
        if (b.driveFolderId) this.budgetFolderLink = `https://drive.google.com/drive/folders/${b.driveFolderId}`;
        this.isBudgetInEditableStatus = BudgetModel.isEditableStatus(b);
        this.areScopesSet = BudgetModel.areScopesSet(b);
        this.permissions.modifyAuthorizationsGranted = this.hasModifyAuth();
        if (b.status === 'closed') {
            this.permissions.canAddSubBudget = this.permissions.modifyAuthorizationsGranted;
        }
        console.debug(`${this.logPrefix} Updated page permissions:`, this.permissions);
    }

    private updatePaymentStatus(b: Budget) {
        if (b.project && b.company && !b.project.internal && this.globalSettings?.cosmo.enabled) {
            this.dataService.getBudgetPaymentStatus(
                b.id,
                b.project.projectNr, b.project.finalOfferOrder,
                this.globalSettings.prosperworks.whDomain,
                (b.company.companyId as string)
            ).pipe(take(1)).subscribe();
        }
    }

    public checkFilters(tags) {
        if (tags) {
            return this.tagFilter.length === 0 || tags.some(tag => this.tagFilter.includes(tag));
        } else {
            return this.tagFilter.length === 0;
        }
    }

    private updateGraph() {
        return this.subBudgetValues.pipe(
            tap((val) => {
                const ret = [];
                val.forEach((value: ISubBudgetTotals, key) => {
                    if (value.calculate) {
                        ret.push({
                            name: this.sbCollNameMap.get(key),
                            series: [
                                {name: 'Activities', value: value.activities || 0},
                                {name: 'Materials', value: value.materials || 0}
                            ]
                        });
                    }
                });
                this.subBudgetValuesForChart = ret;
            }));
    }

    private resetVariables() {
        this.budget = null;
        this.exportBudget = null;
        this.exportBudget$.next(null);
        this.importBudget = null;
        this.permissions.canAddSubBudget = false;
        this.createExportBudgetBtn = false;
        this.removeExportLinkBtn = false;
        this.splittedCostsTableDataIta = [];
        this.splittedCostsTableDataExport = [];
        this.costsTableData = [];
    }

    private offerData(splittedCostsTableData: any[]): Offer {
        const oData: Offer = {} as Offer;
        splittedCostsTableData.forEach((el: SplittedCostTableElementV1 | SplittedCostTableElementV2) => {
            switch (el.id) {
                case 'assembly':
                    oData['assemblyAmount'] = el.total;
                    break;
                case 'design':
                    oData['designAmount'] = el.total;
                    break;
                case 'production':
                    oData['productionAmount'] = el.total;
                    break;
                case 'shipping':
                    oData['shippingAmount'] = el.total;
                    break;
                case 'total':
                    oData['offerAmount'] = el.total;
                    break;
            }
        });
        return oData;
    }

    private offerDataExport(splittedCostsTableDataExport: any[]): Offer {
        const oData: Offer = {} as Offer;
        splittedCostsTableDataExport.forEach((el: SplittedCostTableElementV1 | SplittedCostTableElementV2) => {
            switch (el.id) {
                case 'assembly': // installation
                    oData['assemblyAmount'] = el.total;
                    break;
                case 'design':
                    oData['designAmount'] = el.total;
                    break;
                case 'production':
                    oData['productionAmount'] = el.total;
                    break;
                case 'shipping':
                    oData['shippingAmount'] = el.total;
                    break;
                case 'total':
                    oData['offerAmount'] = el.total;
                    break;
            }
        });
        return oData;
    }

    private archiveBudget() {
        this.appState.confirmDialog({confirmMessage: 'Budget will be archived'})
            .then((result) => {
                if (result?.confirm) {
                    this.budget.archivedDate = new Date();
                    this.budget.status = 'archived';
                    console.info(`${this.logPrefix} Archiving budget ${this.budget.id}.`);
                    this.dataService.archiveBudget(this.budget.id, false)
                        .subscribe(res => {
                            console.info(`${this.logPrefix} Budget archived successfully.`, res);
                            this._snackbar.open('Budget archived successfully.', 'Close', {duration: 3000});
                        }, err => {
                            console.error(`${this.logPrefix} Error archiving budget:`, err);
                            this._snackbar.open('Failed to archive budget. Please try again.', 'Close', {duration: 5000});
                        });
                }
            });
    }

    private archiveProject() {
        const dialogRef = this.dialog.open(ArchiveProjectDialogComponent, {
            width: '350px',
            data: this.budget.project
        });

        dialogRef.afterClosed().pipe(
            filter(result => result))
            .subscribe(result => {
                console.info(`${this.logPrefix} Archiving project for budget ${this.budget.id}.`);
                this.dataService.archiveBudget(this.budget.id, true, result.finalInvoiced)
                    .subscribe(res => {
                        console.info(`${this.logPrefix} Project archived successfully.`, res);
                        this._snackbar.open('Project archived successfully.', 'Close', {duration: 3000});
                    }, err => {
                        console.error(`${this.logPrefix} Error archiving project:`, err);
                        this._snackbar.open('Failed to archive project. Please try again.', 'Close', {duration: 5000});
                    });
            });
    }

    public isTimestamp(value: any): value is Timestamp {
        return value instanceof Timestamp;
    }

    // #endregion
}